import { app } from "./app"

export interface AgentServiceFilters {
  city?: number | null
  category?: number | null
  region?: number | null
  serviceName?: string | null
}

export interface AgentServiceFilterOptions {
  translateName: (text?: LocalizableText) => string
}

export interface AgentServiceFilterResult {
  filters: AgentServiceFilters
  language?: string
}

export function parseAgentServiceFilters(
  response: unknown,
): AgentServiceFilterResult | undefined {
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    return undefined
  }

  const record = response as Record<string, unknown>
  const hasFilterField = ["city", "category", "region", "provider"].some(
    (key) => key in record,
  )

  if (!hasFilterField) {
    return undefined
  }

  return {
    filters: {
      city: record.city != null ? Number(record.city) : null,
      category: record.category != null ? Number(record.category) : null,
      region: record.region != null ? Number(record.region) : null,
      serviceName:
        record.provider != null ? String(record.provider).trim() : null,
    },
    language:
      record.language != null ? String(record.language).trim() : undefined,
  }
}

function getServiceNameVariants(name?: LocalizableText): string[] {
  if (!name) return []
  if (typeof name === "string") return [name]
  return Object.values(name).filter(
    (value): value is string => typeof value === "string" && value.trim().length > 0,
  )
}

function normalizeName(value: string): string {
  return value.normalize().toLowerCase().trim()
}

function serviceNameMatches(serviceName: string, searchName: string): boolean {
  const normalizedServiceName = normalizeName(serviceName)
  const normalizedSearchName = normalizeName(searchName)
  if (!normalizedServiceName || !normalizedSearchName) {
    return false
  }
  return (
    normalizedServiceName === normalizedSearchName ||
    normalizedServiceName.includes(normalizedSearchName) ||
    normalizedSearchName.includes(normalizedServiceName)
  )
}

function serviceMatchesSearchName(
  service: Service,
  searchName: string,
  translateName: (text?: LocalizableText) => string,
): boolean {
  const translatedName = translateName(service.name)
  if (serviceNameMatches(translatedName, searchName)) {
    return true
  }

  if (
    getServiceNameVariants(service.name).some((variant) =>
      serviceNameMatches(variant, searchName),
    )
  ) {
    return true
  }

  if (service.provider != null) {
    const provider = app.data.providers?.[service.provider]
    const providerName = translateName(provider?.name)
    if (providerName && serviceNameMatches(providerName, searchName)) {
      return true
    }
    if (
      getServiceNameVariants(provider?.name).some((variant) =>
        serviceNameMatches(variant, searchName),
      )
    ) {
      return true
    }
  }

  return false
}

function matchesOtherFilters(
  service: Service,
  filters: AgentServiceFilters,
): boolean {
  if (filters.city != null && service.cityId !== filters.city) {
    return false
  }
  if (filters.region != null && service.regionId !== filters.region) {
    return false
  }
  if (
    filters.category != null &&
    !service.categories?.some((category) => category === filters.category)
  ) {
    return false
  }
  return true
}

function applyOtherFilters(
  services: Service[],
  filters: AgentServiceFilters,
): Service[] {
  return services.filter((service) => matchesOtherFilters(service, filters))
}

export function filterServicesByAgentFilters(
  services: Service[],
  filters: AgentServiceFilters,
  options?: AgentServiceFilterOptions,
): Service[] {
  let matched = services

  if (filters.serviceName && options?.translateName) {
    matched = matched.filter((service) =>
      serviceMatchesSearchName(service, filters.serviceName!, options.translateName),
    )
  }

  return applyOtherFilters(matched, filters)
}

export interface AgentServiceResultInfo {
  id?: number
  name: string
  address?: string
  city?: string
  region?: string
  description?: string
}

export function buildAgentServiceResults(
  services: Service[],
  translateName: (text?: LocalizableText) => string,
  limit = 5,
): AgentServiceResultInfo[] {
  return services.slice(0, limit).map((service) => ({
    id: service.id,
    name: translateName(service.name),
    address: service.address,
    city: service.city,
    region: service.region,
    description: translateName(service.description),
  }))
}

export function formatAgentServiceResultsMessage(
  params: {
    serviceCount: number
    language?: string
    services: AgentServiceResultInfo[]
  },
): string {
  const lines = [
    `Total services found: ${params.serviceCount}`,
    `Language: ${params.language ?? ""}`,
  ]

  if (params.services.length === 0) {
    lines.push("Services: none")
    return lines.join("\n")
  }

  lines.push("Services:")

  params.services.forEach((service, index) => {
    lines.push(
      `${index + 1}. Name: ${service.name}`,
      `   ID: ${service.id ?? ""}`,
      `   Address: ${service.address ?? ""}`,
      `   City: ${service.city ?? ""}`,
      `   Region: ${service.region ?? ""}`,
      `   Description: ${service.description ?? ""}`,
    )
  })

  return lines.join("\n")
}
